import { useMemo, useState } from 'react';
import {
  useGetUsersQuery,
  useGetOrganizationsQuery,
} from '../../../redux/api';
import type { User as UserModel } from '../../../redux/api';
import { IconSearch } from '../../../components/icons';
import { userStatusBadge } from '../../../config/status';

const fullName = (u: UserModel) => `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();

const initials = (u: UserModel) =>
  `${u.firstName?.[0] ?? ''}${u.lastName?.[0] ?? ''}`.toUpperCase() || '?';

// Unique roles across all of the user's organization memberships.
const userRoles = (u: UserModel): string[] => {
  const roles = Object.values(u.organizations ?? {}).flatMap((org) => org.role ?? []);
  return Array.from(new Set(roles));
};

const userOrgIds = (u: UserModel): string[] => Object.keys(u.organizations ?? {});

const formatDate = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : '—';

const statusTone = userStatusBadge;

const COLUMN_COUNT = 6;

// Admin → Users table. Reads from the GraphQL `users` query (redux/api) and
// resolves each membership's organizationId → name via the `organizations`
// query. Search is applied client-side over the fetched rows.
const Users = () => {
  const {
    data: users = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetUsersQuery();
  const { data: organizations = [] } = useGetOrganizationsQuery();

  const [search, setSearch] = useState('');

  const orgName = (id: string) =>
    organizations.find((org) => org.id === id)?.name ?? id;

  const visibleUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return users;
    return users.filter((user: UserModel) =>
      [user.firstName, user.lastName, user.email, user.status, ...userRoles(user)].some(
        (field) => (field ?? '').toLowerCase().includes(query)
      )
    );
  }, [users, search]);

  const errorMessage = error
    ? (error as Partial<{ message: string }>).message ?? 'Failed to load users'
    : null;

  return (
    <div className="dashboard">
      <div className="dashboard__head">
        <div>
          <h1>Users</h1>
          <p>Manage the people who can access this organization.</p>
        </div>
        <button
          type="button"
          className="btn"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          {isFetching ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <section className="panel">
        <div className="panel__head">
          <div>
            <h2>Team members</h2>
            <p className="panel__sub">
              Showing {visibleUsers.length} of {users.length}
            </p>
          </div>
          <div className="toolbar">
            <div className="search">
              <span className="search__icon">
                <IconSearch />
              </span>
              <input
                type="search"
                placeholder="Search users…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th><span className="th-sort" style={{ cursor: 'default' }}>User</span></th>
                <th><span className="th-sort" style={{ cursor: 'default' }}>Email</span></th>
                <th><span className="th-sort" style={{ cursor: 'default' }}>Role</span></th>
                <th><span className="th-sort" style={{ cursor: 'default' }}>Status</span></th>
                <th><span className="th-sort" style={{ cursor: 'default' }}>Organizations</span></th>
                <th><span className="th-sort" style={{ cursor: 'default' }}>Updated</span></th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    Loading users…
                  </td>
                </tr>
              ) : errorMessage ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    <div className="table-error">
                      <span>{errorMessage}</span>
                      <button type="button" className="btn btn--ghost" onClick={() => refetch()}>
                        Try again
                      </button>
                    </div>
                  </td>
                </tr>
              ) : visibleUsers.length === 0 ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    {users.length === 0 ? 'No users yet.' : 'No users match your search.'}
                  </td>
                </tr>
              ) : (
                visibleUsers.map((user: UserModel) => {
                  const roles = userRoles(user);
                  const orgIds = userOrgIds(user);
                  return (
                    <tr key={user.id}>
                      <td>
                        <div className="cell-assignee">
                          <span className="avatar">{initials(user)}</span>
                          <span className="cell-title">{fullName(user) || '—'}</span>
                        </div>
                      </td>
                      <td className="cell-date">{user.email}</td>
                      <td>
                        {roles.length ? (
                          roles.map((role) => (
                            <span key={role} className="pill pill--neutral">
                              {role}
                            </span>
                          ))
                        ) : (
                          <span className="pill pill--neutral">user</span>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${statusTone(user.status)}`}>
                          <span className="badge__dot" />
                          {user.status ?? 'unknown'}
                        </span>
                      </td>
                      <td>{orgIds.length ? orgIds.map(orgName).join(', ') : '—'}</td>
                      <td className="cell-date">{formatDate(user.updatedAt)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default Users;
